import { createContext, useContext, type ReactNode } from "react";
import { z } from "zod";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import type { CongressAnalyticsContext } from "../../tools/analytics/domains/congress.analytics";
import { useLocationState } from "../app/hooks/useLocationState";

/**
 * Attributing a content open to the congress space it was opened FROM.
 *
 * The problem this solves: `playlist_opened` and `content_viewed` are emitted
 * on the DESTINATION page (PlaylistPage, the article/video/infographic pages),
 * not on the card that was tapped. The destination knows only the room and the
 * content id from its URL, so a collection opened inside a congress space was
 * indistinguishable from the same collection opened anywhere else in the room.
 *
 * So the congress identity has to cross a navigation: stamped into router
 * state by the card, read back by the destination, and attached to the event
 * the destination already fires. A property on the existing event, never a new
 * event — a second event would silently make every existing collection metric
 * under-count congress-space opens.
 *
 * This file owns all four pieces of that crossing — the ambient context the
 * cards read, the provider the congress surface mounts, the gate, and the Zod
 * schema for the router state. They are one concept with four consumers, not a
 * junk drawer: the congress domain is their real semantic owner.
 */

/** What crosses the navigation: the congress identity, verbatim. */
export type CongressAttribution = CongressAnalyticsContext;

const CongressAttributionContext = createContext<CongressAttribution | null>(
  null
);

export function CongressAttributionProvider({
  attribution,
  children,
}: {
  attribution: CongressAttribution;
  children: ReactNode;
}) {
  return (
    <CongressAttributionContext.Provider value={attribution}>
      {children}
    </CongressAttributionContext.Provider>
  );
}

/**
 * The congress identity to stamp on a navigation out of this card, or null.
 *
 * THE GATE — `listName` is what makes it safe, and it is load-bearing:
 * the provider's React subtree is WIDER than the congress surface. Every
 * content card renders its own `PlaylistPanel` (the save-to-collection sheet),
 * and that panel renders content cards back — clickable, navigating, and
 * declaring `PLAYLIST_PANEL`. A bare `useContext` read would therefore stamp
 * the congress onto an article opened from the save panel of a congress
 * article, which is a different surface entirely.
 *
 * Gating on the list name the caller already passes means a card attributes
 * only when it was TOLD it is drawing the congress surface. Tree position
 * stops being load-bearing, so nesting a congress card inside anything, or
 * anything inside a congress card, cannot leak. Asserted in both directions by
 * `congressAttribution.test.tsx` — the negative cases are the ones that encode
 * this gate, so removing the check below must turn them red.
 */
export function useCongressAttribution(
  listName: AnalyticsItemList
): CongressAttribution | null {
  const attribution = useContext(CongressAttributionContext);
  if (listName !== AnalyticsItemList.CONGRESS_SPACE) return null;
  return attribution;
}

const phaseSchema = z.enum(["evergreen", "pre", "live", "post"]);

/**
 * Router state is an external boundary — `undefined` on direct URL entry, hard
 * reload, and any deep link — so it is parsed, never trusted. `.default(null)`
 * on the field is what makes a cold entry a clean "no congress" rather than a
 * throw.
 */
/** The congress identity, as router state carries it. */
export const congressAttributionSchema = z.object({
  spaceId: z.string(),
  congressName: z.string(),
  phase: phaseSchema,
  roomId: z.string(),
});

const locationStateSchema = z.object({
  congressAttribution: congressAttributionSchema.nullable().default(null),
});

/**
 * Read the congress identity stamped by whichever card navigated here.
 *
 * ACCEPTED — LOST ON COLD ENTRY. Router state does not survive a reload, an
 * external deep link, or iOS Safari discarding it. That is correct rather than
 * tolerated: the metric counts opens REACHED FROM the congress space, and a
 * reload is not one. It matches the `congressEntryPoint` stamp this feature
 * already relies on, which accepts the same loss for the same reason.
 *
 * ACCEPTED — RESTORED ON BROWSER BACK, so navigating back to the destination
 * re-fires the event with the congress id still attached. Also correct: a
 * back-navigation landing on the collection IS a view reached from the
 * congress space. Deduplicating would need session logic and would make this
 * number diverge from `playlist_opened`'s semantics everywhere else in the
 * product — which is the whole reason this shipped as a property on the
 * existing event instead of a new event. Consistency with the base event wins;
 * the board shows total AND unique, so a reader can see the difference.
 */
export function useCongressAttributionFromNavigation(): CongressAttribution | null {
  const { congressAttribution } = useLocationState(locationStateSchema);
  return congressAttribution;
}

/**
 * The crumb a desktop page reached from a congress space shows for it, so the
 * trail says where the reader came from and leads back there.
 */
export function congressBreadcrumb(attribution: CongressAttribution): {
  label: string;
  to: string;
} {
  return {
    label: attribution.congressName,
    to: `/room/congress/${attribution.roomId}/${attribution.spaceId}`,
  };
}
